/**
 * Content sections (non-form) for the RE//FORM PLACE CHI site.
 * All content is preserved from the original client-supplied copy;
 * placeholders are marked `[TO REPLACE]`.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Flame,
  BookOpenText,
  HandHeart,
  Compass,
  Sun,
  Users,
  HeartHandshake,
  Sparkles,
  MapPin,
  Clock,
  Play,
  ArrowRight,
  Church,
  ShieldCheck,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { fetchSermons } from "@/lib/api";
import { ORG_ADDRESS_LINE } from "@/lib/org";

/* ---------------- About / Story ---------------- */
export function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="section bg-[var(--brand-cream)] text-black relative"
    >
      <div className="container-x grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <p className="eyebrow mb-4">Our Story</p>
          <h2 className="font-display font-black uppercase text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-black">
            A House Built
            <br />
            For The <span className="brand-red">Now.</span>
          </h2>
        </div>
        <div className="lg:col-span-7 space-y-5 text-base md:text-lg leading-relaxed text-zinc-700">
          <p>
            RE//FORM PLACE CHI is a Chicago-based 501(‌c)(3) nonprofit faith and
            community organization — a Spirit-filled, apostolic, and prophetic
            church where people meet God, discover purpose, and become who they
            were created to be.
          </p>
          <p>
            We exist to reach souls, root disciples, release destiny, strengthen
            families, and reform culture from the inside out. This isn’t about
            religion. It’s about relationship, revelation, and real
            transformation.
          </p>
          <Link to="/about" className="btn-dark-sharp mt-2" data-testid="about-cta-story">
            Our Mission &amp; History <ArrowRight size={14} className="ml-2 inline" />
          </Link>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-black/10 mt-10">
            {[
              { n: "5", l: "Core Values" },
              { n: "12:30", l: "Sunday CST" },
              { n: "4+", l: "Gatherings" },
              { n: "1", l: "Family" },
            ].map((s) => (
              <div
                key={s.l}
                data-testid={`stat-${s.l.toLowerCase().replace(/\s+/g, "-")}`}
                className="bg-[var(--brand-cream)] p-6"
              >
                <div className="font-display font-black text-4xl md:text-5xl leading-none brand-red">
                  {s.n}
                </div>
                <div className="mt-2 text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500">
                  {s.l}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Service Times ---------------- */
const SERVICES = [
  {
    tag: "Sunday",
    name: "Worship Experience",
    time: "12:30 PM CST",
    desc: "Our main Sunday gathering — worship, prophetic ministry, life-shaping teaching, and community.",
  },
  {
    tag: "Monday",
    name: "The Classroom",
    time: "7:30 PM CST",
    desc: "Midweek discipleship for the hungry — foundations, gifts of the Spirit, spiritual formation.",
  },
  {
    tag: "2nd Thursday",
    name: "Fire Nights",
    time: "Monthly · 7:00 PM",
    desc: "Prayer, presence, and encounter. Come expecting God to move.",
  },
  {
    tag: "4th Sunday",
    name: "Prophetic Night South",
    time: "7:00 PM CST",
    desc: "A prophetic gathering for personal ministry, activation, and impartation.",
  },
];

export function ServiceTimes() {
  return (
    <section
      id="service-times"
      data-testid="service-times-section"
      className="section relative bg-[var(--bg-secondary)]"
    >
      <div className="container-x">
        <div className="max-w-2xl mb-16">
          <p className="eyebrow mb-3">Gather With Us</p>
          <h2 className="font-display font-black uppercase text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-white">
            Service <span className="brand-red">Times</span>
          </h2>
          <p className="mt-4 text-white/60 leading-relaxed">
            Whether it’s your first time or your fiftieth, there’s a seat here
            with your name on it.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#27272A]">
          {SERVICES.map((s, i) => (
            <article
              key={s.name}
              data-testid={`service-card-${i}`}
              className="bg-[var(--bg-secondary)] p-8 lg:p-10 border-l-2 border-transparent hover:border-l-[var(--brand-red)] hover:bg-white/[0.02] transition-colors relative"
            >
              <span className="absolute top-4 right-6 font-display font-black text-6xl text-white/[0.04] leading-none pointer-events-none">
                {String(i + 1).padStart(2, "0")}
              </span>
              <div className="inline-block px-3 py-1 mb-5 border border-[var(--brand-yellow)]/40 bg-[var(--brand-yellow)]/[0.08] text-[10px] font-bold uppercase tracking-[0.2em] text-[var(--brand-yellow)]">
                {s.tag}
              </div>
              <h3 className="font-display font-black uppercase text-2xl leading-tight text-white">
                {s.name}
              </h3>
              <p className="mt-2 flex items-center gap-2 text-sm text-white/50">
                <Clock size={14} className="brand-red" />
                {s.time}
              </p>
              <p className="mt-4 text-sm text-white/60 leading-relaxed">
                {s.desc}
              </p>
            </article>
          ))}
        </div>

        <p className="mt-10 text-sm text-white/45 flex items-center gap-2" data-testid="service-times-address">
          <MapPin size={14} className="brand-red" />
          {ORG_ADDRESS_LINE}
        </p>
      </div>
    </section>
  );
}

/* ---------------- Ministries ---------------- */
const MINISTRIES = [
  {
    tag: "Men",
    title: "REFORMHIM",
    desc: "Building strong men, strong husbands, and strong fathers.",
    img: "https://images.pexels.com/photos/6647037/pexels-photo-6647037.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1200",
  },
  {
    tag: "Women",
    title: "SHEREFORMS",
    desc: "A safe place for women to heal, grow, and be sent.",
    img: "/outreach/shereforms-4.webp",
    imgs: [
      "/outreach/shereforms-4.webp",
      "/outreach/shereforms-6.webp",
      "/outreach/shereforms-1.webp",
      "/outreach/shereforms-7.webp",
      "/outreach/shereforms-2.webp",
      "/outreach/shereforms-3.webp",
      "/outreach/shereforms-5.webp",
    ],
  },
  {
    tag: "Youth · RPK",
    title: "RE//FORM Kids + Youth",
    desc: "Raising a generation who know God and lead their world.",
    img: "/outreach/youth-group.webp",
  },
  {
    tag: "Prayer",
    title: "Intercession & Fire Nights",
    desc: "Prayer teams that carry the atmosphere and the house.",
    img: "https://images.pexels.com/photos/14530767/pexels-photo-14530767.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1200",
  },
  {
    tag: "Worship",
    title: "Worship & Creative",
    desc: "Musicians, singers, and creatives shaping our sound.",
    img: "/media/worship.poster.jpg",
    video: "/media/worship.web.mp4",
  },
  {
    tag: "Outreach",
    title: "GO Teams",
    desc: "The church for the city — street evangelism and community help.",
    img: "https://images.pexels.com/photos/7156163/pexels-photo-7156163.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1200",
  },
];

export function Ministries() {
  return (
    <section
      id="ministries"
      data-testid="ministries-section"
      className="section bg-[var(--bg-primary)]"
    >
      <div className="container-x">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
          <div>
            <p className="eyebrow mb-3">Get Involved</p>
            <h2 className="font-display font-black uppercase text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-white">
              Ministries <span className="brand-red">//</span>
              <br />
              Find Your Place
            </h2>
          </div>
          <p className="max-w-sm text-white/55 leading-relaxed">
            There’s a lane for you here. Grow in community and step into what
            God is calling you to.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#27272A]">
          {MINISTRIES.map((m, i) => (
            <MinistryCard key={m.title} m={m} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

/* Ministry card — supports single `img` or auto-crossfading `imgs` array */
function MinistryCard({ m, index }) {
  const imgs = Array.isArray(m.imgs) && m.imgs.length > 1 ? m.imgs : [m.img];
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (m.video || imgs.length < 2) return;
    const id = setInterval(() => {
      setActive((a) => (a + 1) % imgs.length);
    }, 3500);
    return () => clearInterval(id);
  }, [imgs.length, m.video]);

  return (
    <Link
      to="/new-here"
      data-testid={`ministry-card-${index}`}
      className="relative group bg-black overflow-hidden aspect-[4/5]"
    >
      {m.video ? (
        <video
          src={m.video}
          poster={m.img}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-95 transition-opacity duration-700 group-hover:scale-105"
        />
      ) : (
        imgs.map((src, k) => (
          <img
            key={src}
            src={src}
            alt={m.title}
            loading="lazy"
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 group-hover:scale-105 ${
              k === active ? "opacity-80 group-hover:opacity-95" : "opacity-0"
            }`}
          />
        ))
      )}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(10,10,10,0.15) 0%, rgba(10,10,10,0.9) 75%, rgba(10,10,10,0.98) 100%)",
        }}
      />
      <div className="absolute inset-0 flex flex-col justify-end p-6 lg:p-8">
        <div className="text-[10px] font-bold uppercase tracking-[0.28em] text-[var(--brand-yellow)] mb-2">
          {m.tag}
        </div>
        <h3 className="font-display font-black uppercase text-2xl md:text-3xl leading-none text-white">
          {m.title}
        </h3>
        <p className="mt-3 text-sm text-white/70 max-h-0 group-hover:max-h-24 overflow-hidden transition-[max-height] duration-500">
          {m.desc}
        </p>
        <span className="mt-4 inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-white/70 group-hover:text-white transition-colors">
          Learn More <ArrowRight size={12} />
        </span>
      </div>
      <div className="absolute top-0 left-0 h-1 w-0 bg-[var(--brand-red)] group-hover:w-full transition-all duration-500" />
      {!m.video && imgs.length > 1 && (
        <div className="absolute top-4 right-4 flex gap-1.5">
          {imgs.map((_, k) => (
            <span
              key={k}
              className={`h-1 rounded-full transition-all duration-500 ${
                k === active ? "w-6 bg-white" : "w-3 bg-white/40"
              }`}
            />
          ))}
        </div>
      )}
    </Link>
  );
}

/* ---------------- Culture Pillars ---------------- */
const PILLARS = [
  {
    icon: Flame,
    tag: "P1",
    title: "Presence",
    text: "We build a house of worship, prayer, and encounter — where God shows up and we don’t leave the same.",
  },
  {
    icon: BookOpenText,
    tag: "P2",
    title: "Discipleship",
    text: "We root people in Scripture, spiritual formation, and lifelong maturity, not shallow feel-good faith.",
  },
  {
    icon: HandHeart,
    tag: "P3",
    title: "Community",
    text: "We live in real relationship — healthy, honest, family-strong — believers who show up for each other.",
  },
  {
    icon: Compass,
    tag: "P4",
    title: "Purpose",
    text: "We activate people to walk in destiny, gifts, and calling — sent to their world equipped and unashamed.",
  },
];

export function Pillars() {
  return (
    <section
      id="culture"
      data-testid="pillars-section"
      className="section bg-[var(--brand-cream)] text-black"
    >
      <div className="container-x">
        <div className="max-w-2xl mb-14">
          <p className="eyebrow mb-3">What We Value</p>
          <h2 className="font-display font-black uppercase text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-black">
            The <span className="brand-red">//</span> Pillars
            <br />
            Of Our House
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-px bg-black/10">
          {PILLARS.map((p) => {
            const Icon = p.icon;
            return (
              <div
                key={p.title}
                data-testid={`pillar-${p.title.toLowerCase()}`}
                className="bg-[var(--brand-cream)] hover:bg-white transition-colors p-10 lg:p-14 relative"
              >
                <span className="absolute -top-2 right-6 font-display font-black text-[120px] leading-none text-[color:rgba(196,30,58,0.08)] pointer-events-none">
                  {p.tag}
                </span>
                <div className="w-14 h-14 bg-black flex items-center justify-center brand-red mb-6">
                  <Icon size={22} />
                </div>
                <h3 className="font-display font-black uppercase text-2xl md:text-3xl leading-none text-black">
                  {p.title}
                </h3>
                <div className="mt-1 text-[10px] font-bold uppercase tracking-[0.28em] text-[color:var(--brand-red)]">
                  Pillar {p.tag}
                </div>
                <p className="mt-4 text-zinc-600 leading-relaxed">{p.text}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Leadership ---------------- */
const LEADERS = [
  {
    tag: "Senior Leader",
    name: "Pastor Phillip Varnado",
    img: "/outreach/leader-phil-2.webp",
    imgAlt: "/outreach/leader-phil-1.webp", // candid alt shot
    objectPosition: "center 30%",
    bio: "Pastor Phillip Varnado is a devoted husband, father, preacher, and Senior Leader of RE//FORM PLACE CHI. Known for his steady leadership, wisdom, and heart for people, he carries a strong burden for building healthy individuals, strong families, and lasting spiritual foundations. Alongside his wife, Apostle Prophet Dr. Chantel Varnado, he helps provide apostolic and prophetic leadership to a growing community committed to reaching souls, rooting disciples, releasing destiny, strengthening families, and reforming culture.",
  },
  {
    tag: "Senior Leader",
    name: "Prophet Dr. Chantel Varnado",
    img: "/outreach/leader-chantel.webp",
    objectPosition: "center",
    bio: "Prophet Dr. Chantel Varnado is a preacher, worship leader, author, mentor, and creative visionary with a passion for building people, strengthening families, and helping generations become who God has called them to be. She serves as a Senior Leader of RE//FORM PLACE CHI alongside her husband, Pastor Phillip Varnado, where she leads with a strong apostolic and prophetic grace expressed through prayer, discipleship, leadership development, spiritual formation, and cultural transformation. Her ministry is marked by authenticity, spiritual depth, creativity, and a commitment to creating safe and strong spaces where people can heal, grow, find identity, and answer the call of God.",
  },
];

const TEAM = [
  { dept: "Executive", name: "Dir. Ahyanna Merritt", role: "Executive Assistant + Operations | Children's Church Leader" },
  { dept: "Media", name: "Dir. Jayden Foley-Varnado", role: "Media + Digital" },
  { dept: "Creative", name: "Dir. Yaz Green", role: "Creative + Brand Design" },
  { dept: "Discipleship", name: "Pastor Kayla Sutton", role: "Discipleship + Groups | Youth + Young Adults Pastor" },
  { dept: "Prayer", name: "Prophet Dr. A. Cecilia Beasley", role: "Intercessory Prayer + Prayer Teams" },
  { dept: "Worship", name: "Dir. Xavier Brown", role: "Music + Worship Culture" },
  { dept: "Outreach", name: "Dir. Shanita Clark", role: "Community Outreach & Partnerships" },
  { dept: "Hospitality", name: "Pastor Trenda Brewer", role: "Pastor of Hospitality" },
  { dept: "Finance", name: "Dir. DeAnna Hall", role: "Finance + Operations" },
];

export function Leadership() {
  return (
    <section
      id="leadership"
      data-testid="leadership-section"
      className="section bg-[var(--bg-primary)]"
    >
      <div className="container-x">
        <div className="max-w-2xl mb-14">
          <p className="eyebrow mb-3">Who We Follow</p>
          <h2 className="font-display font-black uppercase text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-white">
            Meet Your <span className="brand-red">Senior Leaders</span>
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
          {LEADERS.map((l, i) => (
            <article
              key={l.name}
              data-testid={`leader-card-${i}`}
              className="relative"
            >
              <div className="absolute top-0 left-0 w-1 h-14 bg-[var(--brand-red)]" />
              <div className="pl-4">
                <div className="aspect-[3/4] w-full bg-[#1A0408] overflow-hidden mb-6">
                  <img
                    src={l.img}
                    alt={l.name}
                    loading="lazy"
                    className="w-full h-full object-cover"
                    style={{ objectPosition: l.objectPosition || "top center" }}
                  />
                </div>
                <div className="text-[10px] font-bold uppercase tracking-[0.28em] text-[var(--brand-yellow)] mb-2">
                  {l.tag}
                </div>
                <h3 className="font-display font-black uppercase text-3xl md:text-4xl leading-none text-white">
                  {l.name}
                </h3>
                <p className="mt-4 text-white/60 leading-relaxed text-[15px]">
                  {l.bio}
                </p>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-24">
          <div className="max-w-2xl mb-10">
            <p className="eyebrow mb-3">Directors & Pastors</p>
            <h3 className="font-display font-black uppercase text-3xl md:text-4xl leading-[0.95] tracking-tight text-white">
              Meet The <span className="brand-red">Team</span>
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#27272A]">
            {TEAM.map((t, i) => (
              <div
                key={t.name}
                data-testid={`team-card-${i}`}
                className="bg-[var(--bg-secondary)] p-8 border-l-2 border-transparent hover:border-l-[var(--brand-red)] transition-colors"
              >
                <div className="text-[10px] font-bold uppercase tracking-[0.28em] text-[var(--brand-yellow)] mb-3">
                  {t.dept}
                </div>
                <h4 className="font-display font-black uppercase text-xl leading-tight text-white">
                  {t.name}
                </h4>
                <p className="mt-2 text-sm text-white/50 leading-relaxed">
                  {t.role}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Events ---------------- */
const EVENTS = [
  { day: "SUN", month: "Weekly", type: "Worship Gathering", title: "Sunday Worship Experience", time: "12:30 PM CST" },
  { day: "MON", month: "Weekly", type: "Midweek Discipleship", title: "The Classroom", time: "7:30 PM CST" },
  { day: "THU", month: "2nd Mo.", type: "Prayer & Encounter", title: "Fire Nights", time: "2nd Thursday · 7:00 PM" },
  { day: "SUN", month: "4th Mo.", type: "Prophetic Gathering", title: "Prophetic Night South", time: "4th Sunday · 7:00 PM CST" },
];

export function Events() {
  return (
    <section
      id="events"
      data-testid="events-section"
      className="section bg-[var(--brand-cream)] text-black"
    >
      <div className="container-x">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <p className="eyebrow mb-3">Stay Connected</p>
            <h2 className="font-display font-black uppercase text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-black">
              What’s <br /> <span className="brand-red">Happening</span>
            </h2>
          </div>
          <Link to="/contact" className="btn-dark-sharp" data-testid="events-view-calendar">
            Ask About An Event
          </Link>
        </div>

        <div className="grid md:grid-cols-2 gap-px bg-black/10">
          {EVENTS.map((e, i) => (
            <div
              key={e.title + i}
              data-testid={`event-item-${i}`}
              className="group bg-white hover:bg-black hover:text-white transition-colors p-8 flex gap-6 items-start cursor-pointer"
            >
              <div className="min-w-[70px] text-center">
                <div className="font-display font-black text-5xl leading-none text-black group-hover:text-white transition-colors">
                  {e.day}
                </div>
                <div className="mt-1 text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--brand-red)] group-hover:text-[var(--brand-yellow)] transition-colors">
                  {e.month}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.28em] text-zinc-500 group-hover:text-white/50 mb-2 transition-colors">
                  {e.type}
                </div>
                <h3 className="font-display font-black uppercase text-2xl leading-tight text-black group-hover:text-white transition-colors">
                  {e.title}
                </h3>
                <p className="mt-2 text-sm text-zinc-600 group-hover:text-white/60 flex items-center gap-2 transition-colors">
                  <Clock size={14} className="text-[var(--brand-red)] group-hover:text-[var(--brand-yellow)]" />
                  {e.time}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Livestream + Recent Messages ---------------- */

/** Reusable click-to-play cover for either the Live Stream OR Latest Message
 * panel. Shows the RE//FORM brand cover graphic with a WATCH button until
 * clicked, then swaps to the YouTube iframe. Avoids YouTube's "video
 * unavailable" screen when the stream / feed isn't loaded yet. */
function WatchCoverPanel({
  label,
  meta,
  dotColor,
  buttonText,
  embedSrc,
  openHref,
  openLabel = "Open on YouTube",
  testId,
}) {
  const [playing, setPlaying] = useState(false);
  return (
    <div className="bg-[var(--bg-secondary)] p-2 lg:p-4">
      <div className="flex items-center gap-3 mb-3 px-2 pt-1">
        <span
          className="w-2 h-2 rounded-full"
          style={{ background: dotColor, ...(dotColor === "var(--brand-red)" ? { animation: "pulse 2s infinite" } : {}) }}
        />
        <span className="text-[10px] font-bold uppercase tracking-[0.28em] text-white/70">
          {label}
        </span>
        {meta && (
          <span className="text-[10px] text-white/40">{meta}</span>
        )}
      </div>
      <div className="relative aspect-video w-full bg-white overflow-hidden">
        {playing ? (
          <iframe
            data-testid={`${testId}-iframe`}
            title={`RE//FORM PLACE CHI ${label}`}
            src={embedSrc}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            className="absolute inset-0 w-full h-full"
          />
        ) : (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            data-testid={`${testId}-play-cover`}
            className="absolute inset-0 w-full h-full group cursor-pointer"
            aria-label={`Play ${label}`}
          >
            <img
              src="/media/watch-cover.png"
              alt="RE//FORM PLACE CHI — A Safe Place to Become. A Strong Place to Belong."
              loading="lazy"
              className="absolute inset-0 w-full h-full object-contain bg-white transition-transform duration-500 group-hover:scale-[1.02]"
            />
            <div
              aria-hidden
              className="absolute inset-0 flex items-end justify-center pb-6 md:pb-10"
              style={{
                background:
                  "linear-gradient(180deg, rgba(255,255,255,0) 55%, rgba(255,255,255,0.9) 100%)",
              }}
            >
              <span className="inline-flex items-center gap-3 px-6 py-3 bg-[var(--brand-red)] group-hover:bg-[var(--brand-red-hover)] text-white text-[11px] font-bold uppercase tracking-[0.22em] transition-colors shadow-lg">
                <Play size={14} /> {buttonText}
              </span>
            </div>
          </button>
        )}
        {playing && openHref && (
          <a
            href={openHref}
            target="_blank"
            rel="noreferrer"
            data-testid={`${testId}-open`}
            className="absolute bottom-3 right-3 z-10 inline-flex items-center gap-2 px-4 py-2 bg-black/70 hover:bg-black text-white text-[10px] font-bold uppercase tracking-[0.22em] transition-colors"
          >
            {openLabel}
          </a>
        )}
      </div>
    </div>
  );
}

function LiveStreamPanel() {
  return (
    <WatchCoverPanel
      label="Live Stream"
      meta="Sundays 12:30 PM CST"
      dotColor="var(--brand-red)"
      buttonText="Watch Live"
      embedSrc="https://www.youtube.com/embed/live_stream?channel=UC15-YROTpCQnf4GtDZK8LlQ&autoplay=1"
      openHref="https://www.youtube.com/@REFORMPlaceCHI/live"
      testId="livestream"
    />
  );
}

function LatestMessagePanel({ item }) {
  const meta = item?.published_at
    ? new Date(item.published_at).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "Most Recent Upload";
  const embedSrc = item
    ? `https://www.youtube.com/embed/${item.video_id}?autoplay=1&rel=0`
    : "https://www.youtube.com/embed/videoseries?list=UU15-YROTpCQnf4GtDZK8LlQ&autoplay=1";
  const openHref = item
    ? item.url
    : "https://www.youtube.com/@REFORMPlaceCHI/videos";
  return (
    <WatchCoverPanel
      label="Latest Message"
      meta={meta}
      dotColor="var(--brand-yellow)"
      buttonText="Play Message"
      embedSrc={embedSrc}
      openHref={openHref}
      openLabel="Open on YouTube"
      testId="latest-message"
    />
  );
}

export function Livestream() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let alive = true;
    fetchSermons(4)
      .then((list) => {
        if (!alive) return;
        setItems(list);
        setStatus(list.length ? "ok" : "empty");
      })
      .catch(() => alive && setStatus("error"));
    return () => {
      alive = false;
    };
  }, []);

  return (
    <section
      id="livestream"
      data-testid="livestream-section"
      className="section bg-[var(--bg-secondary)]"
    >
      <div className="container-x">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <p className="eyebrow mb-3">Word & Worship</p>
            <h2 className="font-display font-black uppercase text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-white">
              Watch <span className="brand-red">Online</span>
            </h2>
            <p className="mt-4 text-white/60 max-w-lg leading-relaxed">
              Watch live every Sunday at 12:30 PM CST, or catch up on the
              most recent message — all right here from our
              @REFORMPlaceCHI YouTube channel.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <a
              href="https://www.youtube.com/@REFORMPlaceCHI/live"
              target="_blank"
              rel="noreferrer"
              className="btn-primary-sharp"
              data-testid="livestream-cta-live"
            >
              <Play size={14} /> Watch Live
            </a>
            <a
              href="https://www.youtube.com/@REFORMPlaceCHI/streams"
              target="_blank"
              rel="noreferrer"
              className="btn-outline-sharp"
              data-testid="livestream-cta-youtube"
            >
              Open YouTube
            </a>
          </div>
        </div>

        {/* Featured live + latest — two-up on desktop, stacked on mobile */}
        <div className="grid lg:grid-cols-2 gap-px bg-[#27272A] mb-10">
          {/* LIVE STREAM — click-to-play cover avoids YouTube's ugly
              "video unavailable" screen when we're not currently live. */}
          <LiveStreamPanel />

          {/* LATEST MESSAGE — same brand cover treatment; loads the most
              recent uploaded video on click (or the uploads playlist as
              a fallback if the sermons feed hasn't reported items yet). */}
          <LatestMessagePanel item={items[0]} />
        </div>

        {/* Recent messages */}
        {status === "loading" && (
          <div className="text-center py-10 text-white/40 text-sm">
            Loading latest messages…
          </div>
        )}
        {status === "error" && (
          <div className="text-center py-10 text-white/40 text-sm">
            Unable to load messages right now.{" "}
            <a
              href="https://www.youtube.com/@REFORMPlaceCHI"
              className="brand-red underline"
              target="_blank"
              rel="noreferrer"
            >
              Watch on YouTube
            </a>
          </div>
        )}
        {status === "ok" && items.length > 1 && (
          <>
            <div className="flex items-center justify-between mb-6">
              <p className="eyebrow eyebrow-muted !text-white/50">
                More Recent Messages
              </p>
              <a
                href="https://www.youtube.com/@REFORMPlaceCHI/videos"
                target="_blank"
                rel="noreferrer"
                className="text-[11px] font-bold uppercase tracking-[0.22em] text-white/50 hover:text-white transition-colors"
                data-testid="livestream-view-all"
              >
                View All →
              </a>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#27272A]">
              {items.slice(1).map((v) => (
              <a
                key={v.video_id}
                data-testid={`sermon-${v.video_id}`}
                href={v.url}
                target="_blank"
                rel="noreferrer"
                className="group bg-[var(--bg-secondary)] block"
              >
                <div className="relative aspect-video overflow-hidden">
                  <img
                    src={v.thumbnail}
                    alt={v.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 grid place-items-center">
                    <span className="w-14 h-14 grid place-items-center bg-[var(--brand-red)]/90 text-white group-hover:bg-[var(--brand-red)] transition-colors">
                      <Play size={18} />
                    </span>
                  </div>
                </div>
                <div className="p-5">
                  <div className="text-[10px] font-bold uppercase tracking-[0.28em] text-[var(--brand-yellow)] mb-2">
                    RE//FORM Place Chicago
                  </div>
                  <h3 className="font-display font-black uppercase text-lg leading-tight text-white line-clamp-2">
                    {v.title}
                  </h3>
                  <p className="mt-2 text-xs text-white/40">
                    {v.published_at
                      ? new Date(v.published_at).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })
                      : ""}
                  </p>
                </div>
              </a>
            ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

/* ---------------- Outreach ---------------- */
// All outreach photos are served from /public/outreach/ after being run
// through /app/scripts/fix_photo_rotation.py (auto-detects rotation).
const OUTREACH_ASSETS = {
  main: "/outreach/story-mural-group-v2.webp", // hero — "This Is Our Story" mural + team
  wcep: "/outreach/wcep.png",
  gallery: [
    { src: "/outreach/englewood-serving.jpg", alt: "Out serving our community — RE//FORM PLACE CHI in Englewood" },
    { src: "/outreach/prayer-on-street.webp", alt: "Praying on the street with a neighbor" },
    { src: "/outreach/distribution-main.webp", alt: "RE//FORM PLACE CHI branded outreach — water and food distribution" },
    { src: "/outreach/pivot-place-kids.webp", alt: "The next generation serving at Feed My Starving Children" },
    { src: "/outreach/shanita-team.webp", alt: "Director Shanita leading a RE//FORM outreach day" },
    { src: "/outreach/reform-shirts-women.webp", alt: "RE//FORM PLACE CHI volunteers in branded tees" },
    { src: "/outreach/family-outreach.webp", alt: "Reaching Chicago families in the neighborhood" },
    { src: "/outreach/green-jacket-cheetos.webp", alt: "Snacks and drinks for the community" },
    { src: "/outreach/cooler-distribution.webp", alt: "Cold drink & food distribution to the community" },
    { src: "/outreach/volunteers-day.webp", alt: "RE//FORM PLACE CHI volunteers on an outreach day" },
    { src: "/outreach/snacks-supplies.webp", alt: "Passing out snacks and supplies to neighbors" },
    { src: "/outreach/family-walking.webp", alt: "Walking through the neighborhood on outreach" },
    { src: "/outreach/christmas-boxes-selfie.webp", alt: "Christmas box drive team selfie" },
    { src: "/outreach/flannel-tree-group.webp", alt: "Serving side-by-side in the community" },
    { src: "/outreach/abundant-blessings.webp", alt: "Community distribution partnership — Abundant Blessings Church" },
    { src: "/outreach/food-bags.webp", alt: "Handing out food bags to the community" },
    { src: "/outreach/outreach-cart.webp", alt: "RE//FORM PLACE CHI branded outreach cart" },
    { src: "/outreach/serving-together.webp", alt: "Church family serving side-by-side" },
    { src: "/outreach/packing-bread.webp", alt: "Packing bread and staples for distribution" },
    { src: "/outreach/chicago-bulls-boxes.webp", alt: "Sorting supplies for distribution" },
    { src: "/outreach/fmsc-observing.webp", alt: "RE//FORM at the Feed My Starving Children packing hall" },
    { src: "/outreach/fmsc-packing-hall.webp", alt: "Feed My Starving Children — packing hall filled with volunteers" },
    { src: "/outreach/fmsc-team-signs.webp", alt: "The team at Feed My Starving Children packing day" },
    { src: "/outreach/fmsc-stats.webp", alt: "Impact — 9,720 meals packed with Feed My Starving Children" },
  ],
};

/**
 * Horizontally scrolling photo strip with:
 *  - Left / right arrow buttons (works on desktop with any input device)
 *  - Vertical mouse-wheel → horizontal scroll (desktop UX)
 *  - Native touch swipe on mobile / trackpad
 *  - Reveals scroll affordance ("Scroll →") hint on desktop
 */
function OutreachGallery({ items }) {
  const scrollerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const updateArrows = () => {
    const el = scrollerRef.current;
    if (!el) return;
    // Threshold accounts for the leading pl-6 md:pl-12 padding — natural
    // resting scrollLeft is ~48px on desktop, so anything below that means
    // the gallery is effectively at the start.
    setCanScrollLeft(el.scrollLeft > 60);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    updateArrows();
    const el = scrollerRef.current;
    if (!el) return;
    const onScroll = () => updateArrows();
    // Attach wheel non-passive so preventDefault() can actually stop the page
    // from also scrolling vertically while the user is scrubbing the strip.
    const onWheel = (e) => {
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        const canGoRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
        const canGoLeft = el.scrollLeft > 1;
        if ((e.deltaY > 0 && canGoRight) || (e.deltaY < 0 && canGoLeft)) {
          e.preventDefault();
          el.scrollLeft += e.deltaY;
        }
      }
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    el.addEventListener("wheel", onWheel, { passive: false });
    window.addEventListener("resize", updateArrows);
    return () => {
      el.removeEventListener("scroll", onScroll);
      el.removeEventListener("wheel", onWheel);
      window.removeEventListener("resize", updateArrows);
    };
  }, []);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const cardWidth = el.querySelector("[data-testid^='outreach-gallery-item']")?.clientWidth || 320;
    el.scrollBy({ left: dir * (cardWidth + 12) * 2, behavior: "smooth" });
  };

  return (
    <div className="relative mt-20">
      <div className="container-x">
        <div className="flex items-end justify-between mb-6">
          <p className="eyebrow" data-testid="outreach-gallery-eyebrow">
            Outreach In Action
          </p>
          <div className="hidden md:flex items-center gap-2">
            <button
              type="button"
              onClick={() => scrollBy(-1)}
              disabled={!canScrollLeft}
              aria-label="Scroll gallery left"
              data-testid="outreach-gallery-prev"
              className="w-10 h-10 grid place-items-center border border-white/15 bg-white/[0.03] text-white/70 hover:text-white hover:border-[var(--brand-red)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              type="button"
              onClick={() => scrollBy(1)}
              disabled={!canScrollRight}
              aria-label="Scroll gallery right"
              data-testid="outreach-gallery-next"
              className="w-10 h-10 grid place-items-center border border-white/15 bg-white/[0.03] text-white/70 hover:text-white hover:border-[var(--brand-red)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>
      <div
        ref={scrollerRef}
        style={{ touchAction: "pan-x pan-y", WebkitOverflowScrolling: "touch" }}
        className="pl-6 md:pl-12 flex gap-3 overflow-x-auto pb-8 snap-x scrollbar-hidden"
        data-testid="outreach-gallery"
      >
        {items.map((g, i) => (
          <div
            key={g.src}
            data-testid={`outreach-gallery-item-${i}`}
            className="relative min-w-[280px] sm:min-w-[360px] aspect-[4/5] snap-start border border-[#27272A] overflow-hidden group"
          >
            <img
              src={g.src}
              alt={g.alt}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
            <div
              aria-hidden
              className="absolute inset-x-0 bottom-0 h-24"
              style={{
                background:
                  "linear-gradient(to top, rgba(10,10,10,0.85) 0%, rgba(10,10,10,0) 100%)",
              }}
            />
            <p className="absolute bottom-3 left-3 right-3 text-[11px] text-white/85 font-medium line-clamp-2">
              {g.alt}
            </p>
          </div>
        ))}
        {/* End spacer so the last card can snap fully into view */}
        <div className="min-w-[24px]" aria-hidden />
      </div>
    </div>
  );
}

export function Outreach() {
  return (
    <section
      id="outreach"
      data-testid="outreach-section"
      className="section bg-[var(--bg-primary)]"
    >
      <div className="container-x grid lg:grid-cols-2 gap-14 lg:gap-24 items-start">
        <div className="relative aspect-[4/5] overflow-hidden border border-[#27272A]">
          <img
            src={OUTREACH_ASSETS.main}
            alt="RE//FORM PLACE CHI GO Teams — community distribution outreach"
            loading="lazy"
            className="w-full h-full object-cover"
          />
          <div className="absolute bottom-0 right-0 left-6 md:left-24 bg-[var(--brand-red)] p-6 md:p-8">
            <p className="font-display font-bold uppercase text-lg md:text-2xl leading-tight text-white">
              The Church For The City. <br /> Not Just In It.
            </p>
          </div>
        </div>

        <div>
          <p className="eyebrow mb-3">Community Outreach</p>
          <h2 className="font-display font-black uppercase text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-white">
            GO Teams <span className="brand-red">//</span> On The Streets
          </h2>
          <p className="mt-6 text-white/60 leading-relaxed">
            We take the love of Jesus outside our walls — door to door,
            block by block. Our GO Teams serve Chicago through evangelism,
            partnerships with local schools and shelters, holiday drives,
            and mercy work.
          </p>

          {/* Real, measurable impact — total 2025 outreach numbers */}
          <div className="mt-8 grid grid-cols-3 gap-px bg-[#27272A]" data-testid="outreach-impact">
            {[
              { n: "1,582", l: "Kids Fed" },
              { n: "800+", l: "Boxes Delivered" },
              { n: "9,720", l: "Meals Packed" },
            ].map((s) => (
              <div key={s.l} className="bg-[var(--bg-primary)] p-5">
                <div className="font-display font-black text-3xl md:text-4xl leading-none brand-red">
                  {s.n}
                </div>
                <div className="mt-2 text-[10px] font-bold uppercase tracking-[0.22em] text-white/50">
                  {s.l}
                </div>
              </div>
            ))}
          </div>
          <p className="mt-3 text-[11px] text-white/40">
            2025 community-outreach impact · in partnership with Feed My Starving Children &amp; local Chicago partners
          </p>

          <div className="mt-10 grid sm:grid-cols-2 gap-px bg-[#27272A]">
            {[
              { icon: HeartHandshake, t: "Serve Locally", d: "Meals, hospitality, family support." },
              { icon: Users, t: "WCEP Program", d: "Workforce + Community Engagement — virtual + in-person." },
              { icon: Sun, t: "Street Reach", d: "Neighborhood evangelism and prayer walks." },
              { icon: Sparkles, t: "Holiday Drives", d: "Coat, food, and school-supply drives." },
            ].map((x) => {
              const Icon = x.icon;
              return (
                <div
                  key={x.t}
                  className="bg-[var(--bg-primary)] p-6 flex gap-4"
                  data-testid={`outreach-item-${x.t.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <span className="w-11 h-11 min-w-11 grid place-items-center bg-[var(--brand-red)]/15 brand-red">
                    <Icon size={18} />
                  </span>
                  <div>
                    <h4 className="font-display font-bold uppercase text-white text-sm tracking-wide">
                      {x.t}
                    </h4>
                    <p className="mt-1 text-xs text-white/50 leading-relaxed">
                      {x.d}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          <Link
            to="/serve"
            className="btn-primary-sharp mt-10"
            data-testid="outreach-cta-volunteer"
          >
            Volunteer With GO Teams
          </Link>
        </div>
      </div>

      {/* Outreach In Action — full-width scrolling photo strip */}
      <OutreachGallery items={OUTREACH_ASSETS.gallery} />

      {/* WCEP program callout — the graphic itself */}
      <div className="container-x mt-16">
        <div className="grid lg:grid-cols-5 gap-8 items-center bg-[var(--bg-secondary)] border border-[#27272A]">
          <div className="lg:col-span-2">
            <img
              src={OUTREACH_ASSETS.wcep}
              alt="WCEP — Workforce + Community Engagement Program with Pastor Phil and Prophet Dr. Chantel Varnado"
              loading="lazy"
              className="w-full h-full object-cover"
              data-testid="wcep-graphic"
            />
          </div>
          <div className="lg:col-span-3 p-8 md:p-12">
            <p className="eyebrow mb-3">Now Enrolling · Serious Inquiries</p>
            <h3 className="font-display font-black uppercase text-3xl md:text-5xl leading-[0.95] tracking-tight text-white">
              WCEP <span className="brand-red">//</span>
              <br />
              Workforce + Community
              <br />
              Engagement Program
            </h3>
            <p className="mt-6 text-white/60 leading-relaxed max-w-xl">
              A church in the city, for the city — providing real opportunities
              for our community. Virtual + in-person tracks available.
            </p>
            <a
              href="mailto:info@reformplacechi.org?subject=WCEP%20Inquiry"
              className="btn-primary-sharp mt-8"
              data-testid="wcep-cta-email"
            >
              Inquire About WCEP
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Next Steps / Membership ---------------- */
export function NextSteps() {
  const steps = [
    { icon: Church, t: "Attend A Service", d: "Come as you are — Sunday 12:30 PM CST." },
    { icon: Users, t: "Get Connected", d: "Meet the team and find a group at Connect Point." },
    { icon: BookOpenText, t: "Grow In The Classroom", d: "Monday night discipleship — Foundations track." },
    { icon: ShieldCheck, t: "Become A Member", d: "Complete Membership Class + commitment card." },
  ];
  return (
    <section
      id="next-steps"
      data-testid="next-steps-section"
      className="section bg-[var(--bg-secondary)]"
    >
      <div className="container-x">
        <div className="max-w-2xl mb-14">
          <p className="eyebrow mb-3">Membership · Next Steps</p>
          <h2 className="font-display font-black uppercase text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-white">
            Grow With Us <span className="brand-red">//</span>
            <br /> In 4 Steps
          </h2>
          <p className="mt-4 text-white/60 leading-relaxed">
            Whether you’re brand new or ready to commit, here’s the pathway
            we walk together at RE//FORM PLACE CHI.
          </p>
        </div>

        <ol className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#27272A] list-none p-0">
          {steps.map((s, i) => {
            const Icon = s.icon;
            return (
              <li
                key={s.t}
                data-testid={`next-step-${i}`}
                className="bg-[var(--bg-secondary)] p-8"
              >
                <div className="flex items-center gap-4 mb-6">
                  <span className="font-display font-black text-5xl brand-red leading-none">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="w-11 h-11 grid place-items-center bg-[var(--brand-red)]/15 brand-red">
                    <Icon size={18} />
                  </span>
                </div>
                <h3 className="font-display font-black uppercase text-xl leading-tight text-white">
                  {s.t}
                </h3>
                <p className="mt-3 text-sm text-white/55 leading-relaxed">
                  {s.d}
                </p>
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
}

/* ---------------- Giving ---------------- */
export function Giving() {
  return (
    <section
      id="giving"
      data-testid="giving-section"
      className="section bg-[var(--bg-primary)] relative overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 50% 50%, rgba(196,30,58,0.10) 0%, transparent 65%)",
        }}
      />
      <div className="container-x relative z-10 grid lg:grid-cols-2 gap-14 lg:gap-24 items-center">
        <div>
          <p className="eyebrow mb-3">Partner With Us</p>
          <h2 className="font-display font-black uppercase text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-white">
            Partner With <br /> The <span className="brand-red">Vision</span>
          </h2>
          <p className="mt-6 text-white/60 leading-relaxed">
            Your generosity helps us reach people, serve our city, develop
            leaders, support outreach initiatives, and expand the work of the
            Kingdom throughout Chicago and beyond.
          </p>

          <p className="mt-10 eyebrow eyebrow-muted">Give securely through:</p>
          <div className="mt-4 grid gap-px bg-[#27272A]">
            {[
              { t: "Cash App", d: "$reformplacechi", href: "https://cash.app/$reformplacechi" },
              { t: "Zelle", d: "give@reformplacechi.org", href: "mailto:give@reformplacechi.org" },
              { t: "PayPal", d: "give@reformplacechi.org", href: "mailto:give@reformplacechi.org" },
            ].map((g) => (
              <a
                key={g.t}
                href={g.href}
                target="_blank"
                rel="noreferrer"
                data-testid={`giving-method-${g.t.toLowerCase().replace(/\s+/g, "-")}`}
                className="bg-white/[0.03] hover:bg-[var(--brand-red)]/[0.08] transition-colors p-6 flex items-center gap-5"
              >
                <span className="w-11 h-11 grid place-items-center bg-[var(--brand-red)]/15 brand-red">
                  <HandHeart size={18} />
                </span>
                <div>
                  <div className="font-display font-bold uppercase text-white">
                    {g.t}
                  </div>
                  <div className="text-sm text-white/55">{g.d}</div>
                </div>
              </a>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            <a
              href="https://cash.app/$reformplacechi"
              target="_blank"
              rel="noreferrer"
              className="btn-primary-sharp"
              data-testid="giving-cta-give-now"
            >
              Give Now
            </a>
            <Link to="/outreach" className="btn-outline-sharp" data-testid="giving-cta-learn">
              Learn About Impact
            </Link>
          </div>
        </div>

        <div>
          <div className="bg-white/[0.03] p-8 md:p-10 border-l-4 border-[var(--brand-red)]">
            <blockquote className="font-display font-bold uppercase text-2xl md:text-3xl leading-tight text-white">
              “God loves a cheerful giver. Each of you should give what you have
              decided in your heart.”
            </blockquote>
            <cite className="mt-4 block text-[11px] font-bold uppercase tracking-[0.28em] text-[var(--brand-yellow)] not-italic">
              2 Corinthians 9:7
            </cite>
          </div>
          <ul className="mt-6 grid gap-2 p-6 bg-white/[0.02] border border-[#27272A]">
            {[
              "Sunday Worship Experiences",
              "Youth & Children's Ministry",
              "Chicago Community Outreach",
              "Leadership Development Programs",
              "Media Production & Digital Reach",
              "GO Teams & Street Evangelism",
            ].map((x) => (
              <li
                key={x}
                className="flex items-center gap-3 text-sm text-white/60"
              >
                <span className="w-1.5 h-1.5 bg-[var(--brand-red)]" />
                {x}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Testimonials ---------------- */
const TESTIMONIALS = [
  { name: "Marcus T.", role: "NOVA Young Adults", text: "RE//FORM gave me a community I didn't know I needed. I came broken and found healing, purpose, and a family that genuinely cares about my growth." },
  { name: "Jasmine R.", role: "RE//FORM Worship Team", text: "I walked in not knowing what to expect and left knowing exactly who I was created to be. This place is real." },
  { name: "Danielle W.", role: "RE//FORM Family", text: "My family found our home at RE//FORM. Kids love RPK, my husband is growing through The Classroom, and I've been activated in my prophetic gift." },
  { name: "Kevin L.", role: "Leadership Development", text: "I was done with church. Hurt, burned out, and disconnected. RE//FORM showed me what church is supposed to look like — authentic and Spirit-filled." },
  { name: "Amara S.", role: "GO Teams", text: "Fire Nights changed my life. One encounter with God's presence in that room and everything shifted. I'm walking in freedom today." },
];

export function Testimonials() {
  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="section bg-[var(--bg-tertiary)]"
    >
      <div className="container-x">
        <div className="max-w-2xl mb-14">
          <p className="eyebrow mb-3">Real Stories</p>
          <h2 className="font-display font-black uppercase text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-white">
            Stories Of <br /> <span className="brand-red">Transformation</span>
          </h2>
        </div>

        <div className="flex gap-6 overflow-x-auto pb-6 snap-x snap-mandatory scrollbar-hidden">
          {TESTIMONIALS.map((t, i) => (
            <article
              key={t.name}
              data-testid={`testimonial-${i}`}
              className="min-w-[85%] sm:min-w-[420px] snap-start bg-white/[0.03] border border-[#27272A] p-10 hover:border-[var(--brand-red)]/40 transition-colors"
            >
              <div className="font-display font-black text-6xl leading-none brand-red opacity-80 mb-4">
                “
              </div>
              <p className="text-white/75 leading-relaxed italic">{t.text}</p>
              <div className="mt-8 flex items-center gap-4">
                <div className="w-11 h-11 grid place-items-center bg-[var(--brand-red)] font-display font-black text-white text-lg">
                  {t.name.charAt(0)}
                </div>
                <div>
                  <div className="font-display font-bold uppercase text-white">
                    {t.name}
                  </div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--brand-yellow)]">
                    {t.role}
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
